/**
 * « Documents — {NUM_QUI} » — the side panel listing a premium's documents
 * (fiche 22763), registered as `extranet.premiumDocumentsPanel`.
 *
 * - Design-system `Drawer` (right, `md`) opened from the « Documents » action
 *   of a premium row; the page owns `open` / `onClose` and the
 *   `WS2_EA_TAB_DOCS_QUI` fetch (`id_qui` = `ID_QUI`), this panel is
 *   presentational.
 * - Each document renders from declarative descriptors with stable keys `nom`
 *   (first visible = primary line, `LIB_ADOC`) and `categorieDate`
 *   (« LIB_DS · JJ/MM/AAAA » from `DAT_ADOC`; a missing half is omitted);
 *   labels / visibility / order / tooltips are overridable through
 *   `_layoutOverrides` (`items` / `fields` bucket).
 * - Title « Documents — {numQui} » resolved with `resolveCardTitle` (a
 *   `setTitle` rule may keep the `{numQui}` token) and published with the
 *   descriptors under the id.
 * - Uniform loading / error / « aucune donnée disponible » states.
 * - No « Voir » action, no size (unspecified / excluded by the fiche).
 *
 * Without `onClose` (the admin config page's preview renders the registered
 * component with sample props only) the panel renders inline as a `Card`,
 * so a preview never opens a modal overlay it could not close.
 */

import { useMemo, type ReactNode } from 'react';
import { FileText } from 'lucide-react';

import { List } from '@components/base/data';
import { Icon, Text, Tooltip } from '@components/base/display';
import { Drawer } from '@components/base/feedback';
import { Card, VStack } from '@components/base/layout';
import {
  applyItemOverrides,
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
  type LayoutOverrides,
} from '@components/shared/configurable';

import { CardBodyState, CardTitleRow, type DocumentItem } from '../shared';
import {
  PREMIUM_DOCUMENT_ITEM_FIELDS,
  PREMIUM_DOCUMENTS_PANEL_ID,
  PREMIUM_DOCUMENTS_PANEL_TEXT,
  premiumDocumentFieldText,
  resolvePanelTitle,
} from './premiumDocumentItems';

export interface PremiumDocumentsPanelProps {
  /** Whether the panel is open. */
  open: boolean;
  /** Close request (backdrop, Escape, close button). Omitted ⇒ inline rendering. */
  onClose?: () => void;
  /** `ID_QUI` of the premium whose documents are listed. */
  idQui: number | null;
  /** `NUM_QUI` shown in the title. */
  numQui: string | null;
  /** Documents in API order (`WS2_EA_TAB_DOCS_QUI` view models). */
  documents: readonly DocumentItem[];
  loading?: boolean;
  error?: string | null;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

function withTooltip(text: string, tooltip: string | undefined): ReactNode {
  if (!tooltip) return text;
  return (
    <Tooltip content={tooltip}>
      <span>{text}</span>
    </Tooltip>
  );
}

export function PremiumDocumentsPanel({
  open,
  onClose,
  idQui,
  numQui,
  documents,
  loading = false,
  error = null,
  _layoutOverrides,
}: Readonly<PremiumDocumentsPanelProps>) {
  usePublishLayoutConfig(
    PREMIUM_DOCUMENTS_PANEL_ID,
    buildPublishedConfigFromInfoItems(
      [...PREMIUM_DOCUMENT_ITEM_FIELDS],
      PREMIUM_DOCUMENTS_PANEL_TEXT.title,
    ),
  );

  const visibleFields = useMemo(() => {
    const overrides = _layoutOverrides?.items ?? _layoutOverrides?.fields;
    return [...applyItemOverrides(PREMIUM_DOCUMENT_ITEM_FIELDS, overrides)]
      .filter((field) => field.visible !== false)
      .sort(
        (a, b) =>
          (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY),
      );
  }, [_layoutOverrides]);

  const title = resolvePanelTitle(
    resolveCardTitle(PREMIUM_DOCUMENTS_PANEL_TEXT.title, _layoutOverrides),
    numQui,
  );
  const [primary, ...secondary] = visibleFields;

  const body = (
    <CardBodyState loading={loading} error={error} empty={documents.length === 0}>
      <Card padding="none">
        <div data-testid="premium-documents-list" data-id-qui={idQui ?? undefined}>
          <List dividers>
            {documents.map((document) => (
              <List.Item
                key={document.id}
                leading={<Icon icon={FileText} size="sm" color="primary" />}
              >
                <VStack gap={0} className="min-w-0" data-document-id={document.id}>
                  {primary && (
                    <Text as="span" size="sm" weight="medium" truncate>
                      {withTooltip(premiumDocumentFieldText(primary.key, document), primary.tooltip)}
                    </Text>
                  )}
                  {secondary.map((field) => (
                    <Text key={field.key} as="span" size="xs" color="muted">
                      {withTooltip(premiumDocumentFieldText(field.key, document), field.tooltip)}
                    </Text>
                  ))}
                </VStack>
              </List.Item>
            ))}
          </List>
        </div>
      </Card>
    </CardBodyState>
  );

  if (!onClose) {
    if (!open) return null;
    return (
      <Card padding="none" data-testid="premium-documents-panel">
        {title !== '' && (
          <Card.Header>
            <CardTitleRow title={title} icon={FileText} />
          </Card.Header>
        )}
        <Card.Content>
          <div className="p-4">{body}</div>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Drawer open={open} onClose={onClose} side="right" width="md">
      <Drawer.Header title={title} />
      <Drawer.Body>
        <div data-testid="premium-documents-panel">{body}</div>
      </Drawer.Body>
    </Drawer>
  );
}
